/**
 * Momentum — Core type system
 *
 * Everything in the Boox UI consumes MomentumSignal.
 * Lists sort by it. Detail pages show it. Dashboards aggregate it.
 * AI responses include it. WhatsApp briefs push it.
 *
 * See: docs/__boox__mentor/00_FIRST_PRINCIPLES.md
 * See: docs/__boox__mentor/01_MOMENTUM_SIGNAL_SPEC.md
 */

// ─── Signal Types ────────────────────────────────────────────────────────────

/** Four momentum dimensions × positive/negative */
export type MomentumSignalType =
  // Revenue
  | 'revenue_blocked'
  | 'revenue_ready'
  | 'revenue_collected'
  // Service
  | 'service_risk'
  | 'service_ok'
  | 'service_delivered'
  // Cash
  | 'cash_delayed'
  | 'cash_flowing'
  | 'cash_collected'
  // Growth
  | 'growth_opportunity'
  | 'growth_warning'
  | 'growth_achieved';

export type MomentumSeverity = 'critical' | 'warning' | 'info' | 'positive';

export type MomentumDimension = 'revenue' | 'service' | 'cash' | 'growth';

export type BusinessIntent = 'cash_velocity' | 'service_sla' | 'volume_growth' | 'compliance_first';

// ─── Suggested Action ────────────────────────────────────────────────────────

export interface SuggestedAction {
  label: string;
  labelZh: string;
  /** Navigate to this route */
  route?: string;
  /** Send this as AI chat prompt */
  prompt?: string;
  /** Direct API action identifier */
  apiAction?: string;
  /** Show confirmation before executing */
  requireConfirmation?: boolean;
}

// ─── MomentumSignal ──────────────────────────────────────────────────────────

export interface MomentumSignal {
  id: string;
  type: MomentumSignalType;
  severity: MomentumSeverity;

  /** Monetary impact (try to always quantify) */
  monetaryImpact?: number;
  currency?: string;

  /** Time impact */
  timeImpact?: number;
  timeUnit?: 'hours' | 'days';

  /** Human-readable message */
  message: string;
  messageZh: string;

  /** What to do about it */
  suggestedAction?: SuggestedAction;

  /** Where this signal came from */
  source: {
    pageKey: string;
    entityType?: string;
    entityId?: string;
    ruleId?: string;
  };

  /** When this signal was generated */
  timestamp: string;
}

// ─── MomentumScore (aggregated) ──────────────────────────────────────────────

export interface MomentumDimensions {
  revenueReadiness: number; // 0-100
  serviceHealth: number;    // 0-100
  cashVelocity: number;     // 0-100
  growthSignal: number;     // 0-100
}

export interface MomentumScore {
  /** Composite score 0-100 */
  overall: number;
  dimensions: MomentumDimensions;
  trend: 'up' | 'down' | 'stable';
  trendDelta: number;
  signalCounts: Record<MomentumSeverity, number>;
  topSignals: MomentumSignal[];
  computedAt: string;
}

// ─── MomentumRule (signal producer) ──────────────────────────────────────────

export interface MomentumRule {
  id: string;
  /** Glob pattern matching pageKey */
  pagePattern: string;
  signalType: MomentumSignalType;
  defaultSeverity: MomentumSeverity;
  /** Template with {{var}} placeholders */
  messageTemplate: string;
  messageTemplateZh: string;
  /** JS expression → number (monetary impact) */
  monetaryExpression?: string;
  /** JS expression → number (time impact) */
  timeExpression?: string;
  /** JS expression → boolean (when to fire) */
  condition: string;
  suggestedAction?: {
    labelTemplate: string;
    labelTemplateZh: string;
    route?: string;
    prompt?: string;
    apiAction?: string;
    requireConfirmation?: boolean;
  };
  /** Only fire for these business intents (empty = all) */
  intents?: BusinessIntent[];
  /** Lower = higher priority */
  priority: number;
}

// ─── MomentumConfig (tenant-level) ──────────────────────────────────────────

export interface MomentumConfig {
  intent: BusinessIntent;
  weights: MomentumDimensions;
  thresholds: MomentumThresholds;
  disabledRules: string[];
  extraRules: MomentumRule[];
}

export interface MomentumThresholds {
  invoiceDelayWarnHours: number;
  invoiceDelayCriticalHours: number;
  slaRiskWindowHours: number;
  arOverdueWarnDays: number;
  arOverdueCriticalDays: number;
  capacityWarnPct: number;
  customerDeclinePct: number;
  dsoTargetDays: number;
}

// ─── Dimension helpers ───────────────────────────────────────────────────────

const SIGNAL_TO_DIMENSION: Record<MomentumSignalType, MomentumDimension> = {
  revenue_blocked: 'revenue',
  revenue_ready: 'revenue',
  revenue_collected: 'revenue',
  service_risk: 'service',
  service_ok: 'service',
  service_delivered: 'service',
  cash_delayed: 'cash',
  cash_flowing: 'cash',
  cash_collected: 'cash',
  growth_opportunity: 'growth',
  growth_warning: 'growth',
  growth_achieved: 'growth',
};

export function signalDimension(type: MomentumSignalType): MomentumDimension {
  return SIGNAL_TO_DIMENSION[type];
}

const SEVERITY_ORDER: Record<MomentumSeverity, number> = {
  critical: 0,
  warning: 1,
  info: 2,
  positive: 3,
};

/** Sort signals by severity (critical first) then by monetary impact */
export function sortSignals(signals: MomentumSignal[]): MomentumSignal[] {
  return [...signals].sort((a, b) => {
    const sevDiff = SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity];
    if (sevDiff !== 0) return sevDiff;
    return (b.monetaryImpact ?? 0) - (a.monetaryImpact ?? 0);
  });
}
